import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Shield, Lock } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "gender", label: "Gender" },
  { key: "ethnicity", label: "Ethnicity" },
  { key: "veteran_status", label: "Veteran status" },
  { key: "disability_status", label: "Disability status" },
];

export const DiversityForm = () => {
  const [options, setOptions] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/diversity/options").then((r) => setOptions(r.data)).catch(() => {});
    api.get("/diversity/me").then((r) => setValues(r.data || {})).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/diversity/me", values);
      toast.success("Diversity preferences saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!options) return null;

  return (
    <div className="mt-8 border border-white/10 bg-[#111827] rounded-xl p-6" data-testid="diversity-form">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="mono-label flex items-center gap-1.5">
            <Shield className="w-3 h-3" /> voluntary self-report
          </div>
          <h3 className="font-display text-xl font-semibold mt-2">Diversity (optional)</h3>
          <p className="text-sm text-slate-400 mt-1 max-w-lg">
            Recruiters only ever see anonymised aggregates — never your individual
            answers. This helps them measure pipeline diversity and reduce bias.
            Leave blank if you'd rather not answer.
          </p>
        </div>
        <div className="flex items-center gap-1 text-[11px] text-slate-500 font-mono uppercase">
          <Lock className="w-3 h-3" /> private · aggregate-only
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-5">
        {FIELDS.map((f) => (
          <div key={f.key} className="space-y-2">
            <div className="text-sm text-slate-300">{f.label}</div>
            <Select
              value={values[f.key] || ""}
              onValueChange={(v) => setValues({ ...values, [f.key]: v })}
            >
              <SelectTrigger
                data-testid={`diversity-${f.key}`}
                className="bg-[#0B0F19] border-white/10"
              >
                <SelectValue placeholder="Not answered" />
              </SelectTrigger>
              <SelectContent className="bg-[#111827] border-white/10 text-slate-100">
                {(options[f.key] || []).map((opt) => (
                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>

      <div className="flex justify-end mt-5">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="diversity-save-btn"
          className="bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-full px-5"
        >
          {saving ? "Saving…" : "Save diversity"}
        </Button>
      </div>
    </div>
  );
};
